import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, Select } from '@/components/ui/field';
import { apiMessage, pruneBody } from '../helpers';
import { planLimitRefusal, type PlanLimitRefusal } from '../planLimits';
import {
  EMPTY_SCOPE,
  isScopeComplete,
  scopeIdOf,
  type Role,
  type RoleDetail,
  type ScopeSelection,
  type Staff,
} from '../types';
import PlanLimitNotice from './PlanLimitNotice';
import ScopePicker from './ScopePicker';

interface Props {
  open: boolean;
  staff: Staff | null;
  onClose: () => void;
  onSaved: () => void;
}

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  phone: string;
  locale: string;
  password: string;
  pin: string;
  avatarUrl: string;
  isActive: boolean;
}

const BLANK: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  username: '',
  phone: '',
  locale: 'en',
  password: '',
  pin: '',
  avatarUrl: '',
  isActive: true,
};

/** One labelled band of the form — the frame's cards, inside a dialog. */
function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4 border-t pt-4 first:border-t-0 first:pt-0">
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        {hint && <p className="mt-0.5 text-xs text-ink-muted">{hint}</p>}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </section>
  );
}

/**
 * Create or edit a staff profile. Credentials only appear on create — changing
 * an existing one goes through its own audited endpoint, which is why
 * `UpdateUserDto` omits `password` and `pin` entirely.
 *
 * ## What the Staff Create frame asks for that nothing can store
 *
 * `User` is `{email, phone, username, passwordHash, pinHash, firstName,
 * lastName, avatarUrl, locale, isActive, lastLoginAt}` and `CreateUserDto`
 * declares exactly ten properties. **There is no employment model.** National ID
 * / Iqama, date of birth, start date, employment type, base salary, default
 * shift and days off are seven fields with no column, no DTO entry and no
 * endpoint between them; with `forbidNonWhitelisted` each one would 400 the
 * create and take the whole form down. They are not built and no shape is
 * invented for them — an HR module is a piece of work, not seven columns.
 *
 * The three `Access & Permissions` toggles (POS / Kitchen Display / Manager
 * Dashboard) are not built either, and that one is a contradiction rather than a
 * gap: §24.3 is granular `resource.action` keys, never role enums. "POS access"
 * is not a permission key — it follows from the role's permission set plus the
 * presence of a PIN. Wiring those toggles would reintroduce exactly the model
 * the schema comment refuses.
 *
 * `Required documents` has no attachment model at all. `Staff photo` is the one
 * that half-exists: `avatarUrl` IS on the DTO, as a string, so the field is here
 * — but there is no upload endpoint anywhere in the API for a user, so it takes
 * a URL somebody else hosts rather than a file.
 *
 * ## What the frame drops that has to stay
 *
 * `Username` (a login identifier, `@@unique([tenantId, username])`), `Locale`
 * (drives the staff member's own UI language and their notifications),
 * `Password` (the dashboard credential — the frame offers only a PIN) and the
 * `Active` control are all kept.
 *
 * Scope is the important one. The frame collapses `RoleAssignment (userId,
 * roleId, scopeLevel, scopeId)` over a seven-level `ScopeLevel` to a single
 * `Branch` select. So the grant here uses the real `ScopePicker`, and it is one
 * grant at create time, not the whole story: a user holds a *list* of
 * assignments and the Assignments tab is where they are managed.
 */
export default function StaffFormDialog({ open, staff, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const [form, setForm] = useState<FormState>(BLANK);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  /** The plan ceiling this venue just walked into, if that is what refused the save. */
  const [planLimit, setPlanLimit] = useState<PlanLimitRefusal | null>(null);

  /** The optional first role grant. Create-only; edits go through Assignments. */
  const [roleId, setRoleId] = useState('');
  const [scope, setScope] = useState<ScopeSelection>(EMPTY_SCOPE);

  const canGrant = can('users.update');
  const rolesQuery = useApi<unknown>(open && !staff && canGrant ? '/roles' : null, { limit: 200 });
  const roles = rowsOf<Role>(rolesQuery.data).rows;

  /**
   * `UsersService.grantRole` runs a server-side escalation guard over the ROLE's
   * whole permission set — it refuses any key the actor does not hold — so which
   * role is picked decides whether the grant is possible at all. The list rows
   * carry only a count, so the chosen role's keys are read here and the refusal
   * is shown before the request rather than after the user has been created.
   */
  const roleDetailQuery = useApi<RoleDetail>(roleId ? `/roles/${roleId}` : null);
  const chosenRole = roleDetailQuery.data?.id === roleId ? roleDetailQuery.data : null;
  const escalating = useMemo(
    () => (chosenRole ? (chosenRole.permissionKeys ?? []).filter((key) => !can(key)) : []),
    [chosenRole, can],
  );

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setRoleId('');
    setScope(EMPTY_SCOPE);
    setForm(
      staff
        ? {
            ...BLANK,
            firstName: staff.firstName,
            lastName: staff.lastName ?? '',
            email: staff.email ?? '',
            username: staff.username ?? '',
            phone: staff.phone ?? '',
            locale: staff.locale || 'en',
            avatarUrl: staff.avatarUrl ?? '',
            isActive: staff.isActive,
          }
        : BLANK,
    );
  }, [open, staff]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  /** Why the grant half would be refused, or null when it is fine to send. */
  const grantBlock: string | null =
    !roleId
      ? null // no role chosen is a valid outcome: the profile saves on its own
      : !isScopeComplete(scope)
        ? t('settings.assignments.scopeIncomplete')
        : roleDetailQuery.error
          ? roleDetailQuery.error
          : !chosenRole
            ? t('settings.assignments.roleLoading')
            : escalating.length > 0
              ? t('settings.assignments.blockedEscalation', { n: escalating.length })
              : null;

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!form.firstName.trim()) next.firstName = t('settings.validation.required');
    if (!staff && form.password && form.password.length < 8) {
      next.password = t('settings.validation.passwordLength');
    }
    if (!staff && form.pin && !/^\d{4,6}$/.test(form.pin)) {
      next.pin = t('settings.validation.pinFormat');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async () => {
    if (!validate() || grantBlock) return;
    setSaving(true);
    setPlanLimit(null);
    try {
      const shared = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        username: form.username.trim(),
        phone: form.phone.trim(),
        avatarUrl: form.avatarUrl.trim(),
        locale: form.locale,
      };
      if (staff) {
        await api.patch(`/users/${staff.id}`, {
          ...pruneBody(shared),
          isActive: form.isActive,
        });
        toast.success(t('settings.staff.updated'));
      } else {
        const { data } = await api.post<Staff>('/users', {
          ...pruneBody({ ...shared, password: form.password, pin: form.pin }),
          isActive: form.isActive,
        });
        toast.success(t('settings.staff.created'));

        /**
         * The role is a SECOND request — it is not on `CreateUserDto`, and
         * sending it there would 400 the create. Which means the two can fail
         * apart: the person exists with no role. That is a real state (they
         * simply cannot do anything yet), so it is reported as itself rather
         * than rolled back or swallowed, and the dialog still closes on the
         * profile that did save.
         */
        if (roleId && data?.id) {
          try {
            await api.post(`/users/${data.id}/roles`, {
              roleId,
              scopeLevel: scope.level,
              ...(scopeIdOf(scope) ? { scopeId: scopeIdOf(scope) } : {}),
            });
            toast.success(t('settings.assignments.granted'));
          } catch (error) {
            toast.error(
              apiMessage(
                error,
                t('settings.staff.grantFailedAfterCreate'),
              ),
            );
          }
        }
      }
      onSaved();
      onClose();
    } catch (error) {
      // A staff account is metered by the plan too. Same treatment as a branch: a panel that
      // stays and names the way out, rather than the server's English in a red toast.
      const refusal = planLimitRefusal(error);
      setPlanLimit(refusal);
      if (!refusal) toast.error(apiMessage(error, t('settings.errors.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={staff ? t('settings.staff.editTitle') : t('settings.staff.createTitle')}
      description={t('settings.staff.formHint')}
      className="max-w-2xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={saving || Boolean(grantBlock)}
            title={grantBlock ?? undefined}
          >
            {saving ? t('settings.common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {planLimit && <PlanLimitNotice refusal={planLimit} />}

        <Section
          title={t('settings.staff.personalSection')}
        >
          <Field label={t('settings.staff.firstName')} required error={errors.firstName}>
            <Input
              value={form.firstName}
              onChange={(e) => set('firstName', e.target.value)}
              autoFocus
            />
          </Field>
          <Field label={t('settings.staff.lastName')}>
            <Input value={form.lastName} onChange={(e) => set('lastName', e.target.value)} />
          </Field>
          <Field label={t('settings.staff.phone')}>
            <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} dir="ltr" />
          </Field>
          <Field label={t('settings.staff.email')}>
            <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
          </Field>
          <Field label={t('settings.staff.locale')}>
            <Select value={form.locale} onChange={(e) => set('locale', e.target.value)}>
              <option value="en">{t('settings.locale.en')}</option>
              <option value="ar">{t('settings.locale.ar')}</option>
            </Select>
          </Field>
          {/* `avatarUrl` is a plain string on the DTO and there is no upload
              route for a user anywhere in the API — the storage module serves
              exactly one entity, and it is not this one. So this takes a URL,
              and the hint says so rather than drawing a drop zone that cannot
              accept a file. */}
          <Field
            label={t('settings.staff.avatarUrl')}
            hint={t('settings.staff.avatarUrlHint')}
          >
            <Input
              value={form.avatarUrl}
              dir="ltr"
              inputMode="url"
              onChange={(e) => set('avatarUrl', e.target.value)}
            />
          </Field>
        </Section>

        <Section
          title={t('settings.staff.accessSection')}
          hint={
            staff
              ? t('settings.staff.credentialsElsewhere')
              : undefined
          }
        >
          <Field label={t('settings.staff.username')}>
            <Input value={form.username} onChange={(e) => set('username', e.target.value)} />
          </Field>

          {!staff && (
            <>
              <Field
                label={t('settings.staff.password')}
                hint={t('settings.staff.passwordHint')}
                error={errors.password}
              >
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={form.password}
                  onChange={(e) => set('password', e.target.value)}
                />
              </Field>
              <Field
                label={t('settings.staff.pin')}
                hint={t('settings.staff.pinHint')}
                error={errors.pin}
              >
                <Input
                  inputMode="numeric"
                  autoComplete="off"
                  value={form.pin}
                  onChange={(e) => set('pin', e.target.value)}
                  dir="ltr"
                />
              </Field>
            </>
          )}

          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={form.isActive}
              onChange={(e) => set('isActive', e.target.checked)}
            />
            {t('settings.staff.isActive')}
          </label>
        </Section>

        {!staff && canGrant && (
          <Section
            title={t('settings.staff.roleSection')}
            hint={t('settings.staff.roleSectionHint')}
          >
            <Field label={t('settings.assignments.role')}>
              <Select
                value={roleId}
                disabled={rolesQuery.loading}
                onChange={(e) => setRoleId(e.target.value)}
              >
                <option value="">
                  {rolesQuery.loading ? t('common.loading') : t('settings.common.none')}
                </option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </Select>
            </Field>

            {roleId && (
              <div className="sm:col-span-2 space-y-3">
                <ScopePicker value={scope} onChange={setScope} disabled={saving} />
                {escalating.length > 0 && (
                  <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-400">
                    {t('settings.assignments.blockedEscalationHelp', {
                      role: chosenRole?.name ?? '',
                      n: escalating.length,
                      keys: escalating.join(', '),
                    })}
                  </p>
                )}
              </div>
            )}
          </Section>
        )}
      </div>
    </Dialog>
  );
}
